import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { CopyButton } from '../components/CopyButton'
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, DownloadIcon } from '../components/icons'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { formatBytes } from '../lib/attachments'
import { isTypingTarget } from '../lib/keys'
import { DocumentView } from './DocumentView'
import { ImageView } from './ImageView'
import type { PreviewItem } from './types'

/**
 * Ek onizleme modal'i (plan §7.6b).
 *
 * ## Tek bilesen, IKI kaynak
 *
 * Ayni modal hem composer'daki BEKLEYEN chip'lerde (kaynak yerel bir `File`,
 * `URL.createObjectURL` ile) hem de gonderilmis mesajlarin eklerinde
 * (kaynak sunucu URL'i) calisiyor. Iki ayri modal yazmak, ikisinin
 * kacinilmaz olarak ayrismasi demek olurdu - ve kullanicinin gondermeden
 * once dosyayi dogrulamasi tam olarak bu bilesenin isi.
 *
 * ## Erisilebilirlik PAZARLIKSIZ
 *
 * `role="dialog"` + `aria-modal`, odak modal'a tasiniyor, `Tab` iceride
 * donuyor, kapanista odak ACAN ogeye geri donuyor, `Esc` ve backdrop
 * kapatiyor, `<body>` scroll'u kilitleniyor (iOS icin pozisyon korunarak),
 * `prefers-reduced-motion`da animasyon yok. Hepsi `useFocusTrap` icinde -
 * modal basina yeniden yazilirsa biri mutlaka atlanir.
 *
 * ## Portal
 *
 * `document.body`ye render ediliyor: mesaj listesinin `overflow`/`transform`
 * baglami modal'i KIRPARDI.
 */
export function AttachmentModal({
  items,
  startIndex = 0,
  onClose,
}: {
  items: PreviewItem[]
  startIndex?: number
  onClose(): void
}) {
  const { t } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(startIndex)
  // `DocumentView` bildiriyor: sadece belge sekmesinde VE gercekten
  // kopyalanacak metin varken dolu. Baslikta indir/kapat'in yanina
  // konabilmesi icin buraya tasindi - bkz. `DocumentView`in dosya basi notu.
  const [copyText, setCopyText] = useState<string | null>(null)

  useFocusTrap(container, { active: true, onClose })

  const item = items[index]
  const many = items.length > 1

  const go = useCallback(
    (delta: number) => {
      setIndex((current) => {
        const next = current + delta
        // DONGU YOK: son ogede saga basmak basa atlamamali - kullanici kac
        // ek oldugunu sayacla goruyor ve dongu "hepsini gordum mu"
        // sorusunu belirsizlestirir.
        if (next < 0 || next >= items.length) return current
        return next
      })
    },
    [items.length],
  )

  // Ok tuslariyla gezinme. `capture` DEGIL: odak tuzagi `Tab`/`Esc`i
  // yakaliyor, buradaki dinleyici yalnizca oklarla ilgileniyor ve bir input
  // icindeyken devreye GIRMEMELI.
  useEffect(() => {
    if (!many) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        go(-1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        go(1)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [many, go])

  if (!item) return null

  return createPortal(
    <div
      className="preview-backdrop"
      onClick={(event) => {
        // Yalnizca BACKDROP'a tiklandiginda kapaniyor. Icerige tiklamak
        // (ornegin resmi zoom'lamak) kapatmamali.
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        // Video KENDI boyutunda gosteriliyor: modal sabit 1100 piksel
        // genisken 640 piksellik bir klibin sagi bos kaliyordu (kullanici
        // geri bildirimi). Resimde durum farkli - `ImageView` sahneyi
        // dolduruyor ve bosluk gorunmuyor.
        className={item.kind === 'video' ? 'preview preview-fit' : 'preview'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-title"
        ref={container}
        tabIndex={-1}
      >
        <header className="preview-head">
          <div className="preview-meta">
            <h2 id="preview-title">{item.filename}</h2>
            <p>
              {item.mime}
              {/* Boyutu bilinmeyen ek (eski arac kartlari) "0 B" gostermesin. */}
              {item.byteSize > 0 && ` · ${formatBytes(item.byteSize)}`}
              {many && ' · '}
              {many && (
                <span className="preview-count" aria-live="polite">
                  {index + 1} / {items.length}
                </span>
              )}
            </p>
          </div>
          <div className="preview-actions">
            {copyText && <CopyButton text={copyText} label={t('attach.doc.copyText')} iconOnly />}
            {item.downloadUrl && (
              <a
                className="copy copy-icon-only"
                href={item.downloadUrl}
                download={item.filename}
                aria-label={t('common.download')}
              >
                <DownloadIcon size={16} />
              </a>
            )}
            <button
              type="button"
              className="copy copy-icon-only"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <CloseIcon size={16} />
            </button>
          </div>
        </header>

        <div className="preview-body">
          {/*
            Gezinme oklari icerigin SAGINDA ve SOLUNDA, yazisiz ve kucuk
            (kullanici istegi). Sayac altta kucuk bir etiket olarak duruyor:
            "hepsini gordum mu" sorusunun cevabi ekranda kalmali.
          */}
          {many && (
            <button
              type="button"
              className="preview-arrow preview-arrow-prev"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label={t('attach.modal.prev')}
            >
              <ChevronLeftIcon size={18} />
            </button>
          )}
          {item.kind === 'image' ? (
            <ImageView item={item} />
          ) : item.kind === 'video' ? (
            // Video KULLANICI icin oynatiliyor; model yalnizca kareleri ve
            // konusma metnini goruyor (bkz. services/video_analysis.py).
            <video className="preview-video" src={item.previewUrl} controls preload="metadata">
              <track kind="captions" />
            </video>
          ) : item.kind === 'audio' ? (
            // Sesli mesaj: kayit + altinda transkript (detay ucundaki metin).
            <div className="preview-audio">
              <audio src={item.previewUrl} controls preload="metadata" />
              <DocumentView item={item} onTextChange={setCopyText} />
            </div>
          ) : (
            <DocumentView item={item} onTextChange={setCopyText} />
          )}
          {many && (
            <button
              type="button"
              className="preview-arrow preview-arrow-next"
              onClick={() => go(1)}
              disabled={index === items.length - 1}
              aria-label={t('attach.modal.next')}
            >
              <ChevronRightIcon size={18} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
